import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import {
  buildExport,
  downloadFile,
  previewExport,
  type DataKind,
  type Format,
  type Range,
} from '@/db/export'

const KINDS: { id: DataKind; label: string; description: string }[] = [
  { id: 'recovery', label: 'Recovery', description: 'Score, HRV, RHR, SpO₂, skin temp' },
  { id: 'cycles', label: 'Cycles (daily)', description: 'Strain, energy, avg/max HR' },
  { id: 'sleep', label: 'Sleep', description: 'Stages, performance, consistency, sleep need' },
  { id: 'workouts', label: 'Workouts', description: 'Sport, duration, HR zones, distance' },
  { id: 'profile', label: 'Profile', description: 'Name and email' },
  { id: 'body', label: 'Body measurements', description: 'Height, weight, max HR' },
]

const RANGES: { id: Range; label: string }[] = [
  { id: 'week', label: 'Last week' },
  { id: 'month', label: 'Last month' },
  { id: 'year', label: 'Last year' },
  { id: 'all', label: 'Everything' },
]

const FORMATS: { id: Format; label: string; description: string }[] = [
  { id: 'csv', label: 'CSV', description: 'One spreadsheet per data type' },
  { id: 'html', label: 'HTML', description: 'Single self-contained page' },
  { id: 'markdown', label: 'Markdown', description: 'Tables you can paste anywhere' },
]

export default function Export() {
  const [kinds, setKinds] = useState<DataKind[]>(['recovery', 'cycles', 'sleep', 'workouts'])
  const [range, setRange] = useState<Range>('month')
  const [format, setFormat] = useState<Format>('csv')
  const [preview, setPreview] = useState<{ kind: DataKind; rows: number }[]>([])
  const [exporting, setExporting] = useState(false)
  const [done, setDone] = useState<string | null>(null)

  const opts = useMemo(() => ({ kinds, range, format }), [kinds, range, format])

  useEffect(() => {
    if (kinds.length === 0) {
      setPreview([])
      return
    }
    let cancelled = false
    previewExport(opts).then((p) => {
      if (!cancelled) setPreview(p.kinds)
    })
    return () => {
      cancelled = true
    }
  }, [opts, kinds.length])

  const totalRows = preview.reduce((a, p) => a + p.rows, 0)

  const toggleKind = (k: DataKind) => {
    setDone(null)
    setKinds((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]))
  }

  const onExport = async () => {
    setExporting(true)
    setDone(null)
    try {
      const files = await buildExport(opts)
      for (const f of files) {
        downloadFile(f.filename, f.mime, f.content)
        // Small gap so browsers don't drop concurrent downloads
        await new Promise((r) => setTimeout(r, 250))
      }
      setDone(
        files.length === 1
          ? `Downloaded ${files[0].filename}`
          : `Downloaded ${files.length} files`,
      )
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h1 className="text-2xl font-semibold">Export your data</h1>
        <p className="text-sm text-stone-500 mt-1">
          Download your Whoop history in CSV, HTML, or Markdown. Everything's already in your
          browser; nothing leaves your device.
        </p>
      </div>

      <Section title="Time range">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {RANGES.map((r) => (
            <Pill
              key={r.id}
              label={r.label}
              active={range === r.id}
              onClick={() => {
                setDone(null)
                setRange(r.id)
              }}
            />
          ))}
        </div>
      </Section>

      <Section title="Data">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {KINDS.map((k) => {
            const previewCount = preview.find((p) => p.kind === k.id)?.rows
            const checked = kinds.includes(k.id)
            return (
              <button
                key={k.id}
                onClick={() => toggleKind(k.id)}
                className={clsx(
                  'flex items-start gap-3 text-left rounded-lg border px-4 py-3 transition-colors',
                  checked
                    ? 'border-brand bg-brand/10'
                    : 'border-stone-200 bg-white shadow-sm hover:border-stone-300',
                )}
              >
                <div
                  className={clsx(
                    'mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                    checked ? 'bg-brand border-brand' : 'border-stone-300',
                  )}
                >
                  {checked && (
                    <svg viewBox="0 0 12 12" className="w-3 h-3 text-white" fill="none">
                      <path
                        d="M2 6.5l2.5 2.5L10 3.5"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-baseline gap-2">
                    {k.label}
                    {previewCount != null && checked && (
                      <span className="text-xs text-stone-500 tabular-nums">
                        {previewCount.toLocaleString()} rows
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">{k.description}</div>
                </div>
              </button>
            )
          })}
        </div>
      </Section>

      <Section title="Format">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {FORMATS.map((f) => (
            <button
              key={f.id}
              onClick={() => {
                setDone(null)
                setFormat(f.id)
              }}
              className={clsx(
                'text-left rounded-lg border p-4 transition-colors',
                format === f.id
                  ? 'border-brand bg-brand/10'
                  : 'border-stone-200 bg-white shadow-sm hover:border-stone-300',
              )}
            >
              <div className="text-sm font-medium">{f.label}</div>
              <div className="text-xs text-stone-500 mt-0.5">{f.description}</div>
            </button>
          ))}
        </div>
      </Section>

      <div className="flex items-center justify-between gap-4 pt-2 border-t border-stone-200">
        <div className="text-sm text-stone-500">
          {kinds.length === 0 ? (
            'Pick at least one data type.'
          ) : (
            <span>
              {totalRows.toLocaleString()} rows ·{' '}
              {format === 'csv' ? `${kinds.length} file${kinds.length === 1 ? '' : 's'}` : '1 file'}
            </span>
          )}
        </div>
        <button
          onClick={onExport}
          disabled={exporting || kinds.length === 0 || totalRows === 0}
          className="px-5 py-2 rounded-lg bg-brand text-white font-medium hover:bg-brand-fg disabled:bg-stone-200 disabled:text-stone-400 transition-colors shadow-sm"
        >
          {exporting ? 'Exporting…' : 'Download'}
        </button>
      </div>
      {done && <div className="text-xs text-green-600">{done}</div>}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-stone-500 mb-3">{title}</div>
      {children}
    </div>
  )
}

function Pill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'px-3 py-2 rounded-md text-sm transition-colors text-center',
        active
          ? 'bg-brand text-white'
          : 'bg-white border border-stone-200 text-stone-700 hover:bg-stone-50',
      )}
    >
      {label}
    </button>
  )
}
